<div class="container my-4" *transloco="let t; scope: 'ideas'">
  <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
    <div>
      <h1 class="h2">{{ hub ? t('ideas.hub_title') : t('navigation.ideas') }}</h1>
      <p class="lead mb-0">{{ hub ? t('ideas.hub_intro') : t('ideas.intro') }}</p>
    </div>
    @if (maySubmit()) {
      <a class="btn btn-primary" [routerLink]="[...baseRoute, 'new']">
        <i class="fa-regular fa-lightbulb me-2"></i>{{ t('ideas.submit_an_idea') }}
      </a>
    }
  </div>

  @if (!failure()?.notInvolved) {
    <!-- Stages and tags are both "any of": stages as filled pills, tags as round icon pills with a tag mark. -->
    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <form class="ideas-search" (ngSubmit)="search()">
        <input type="search" class="form-control form-control-sm" name="search" [(ngModel)]="searchText" (ngModelChange)="clearSearchIfEmpty()"
          [placeholder]="t('navigation.search')" [attr.aria-label]="t('navigation.search')">
      </form>
      <ul class="nav nav-pills ideas-stages" role="group" [attr.aria-label]="t('navigation.status')">
        @for (stage of stages; track stage.key) {
          <li class="nav-item">
            <a class="nav-link ideas-stage" role="checkbox" tabindex="0" [class.active]="isStageChosen(stage.key)"
              [attr.aria-checked]="isStageChosen(stage.key)" [class.disabled]="isAnythingLoading()"
              (click)="toggleStage(stage.key)" (keydown.enter)="toggleStage(stage.key)"
              (keydown.space)="toggleStage(stage.key); $event.preventDefault()">{{ t(stage.labelKey) }}</a>
          </li>
        }
      </ul>
      <div class="form-check form-switch mb-0 ms-1">
        <input class="form-check-input" type="checkbox" role="switch" id="ideas-mine" [checked]="state().mine" (change)="toggleMine($any($event.target).checked)">
        <label class="form-check-label" for="ideas-mine">{{ t('ideas.mine') }}</label>
      </div>
      <div class="form-check form-switch mb-0 ms-1">
        <input class="form-check-input" type="checkbox" role="switch" id="ideas-following" [checked]="state().following" (change)="toggleFollowing($any($event.target).checked)">
        <label class="form-check-label" for="ideas-following">{{ t('ideas.only_following') }}</label>
      </div>

      @if (!tagsAsPills()) {
        <div class="btn-group" dropdown [insideClick]="true">
          <button type="button" class="btn btn-sm btn-secondary dropdown-toggle" dropdownToggle aria-controls="ideas-tags-menu">
            <i class="fa-solid fa-tags me-1" aria-hidden="true"></i>{{ t('ideas.tags') }}
            @if (state().tags.length) {
              <span class="badge rounded-pill text-bg-light ms-1">{{ state().tags.length }}</span>
            }
          </button>
          <div id="ideas-tags-menu" *dropdownMenu class="dropdown-menu ideas-tags-menu" role="group" [attr.aria-label]="t('ideas.tags')">
            @for (tag of tagChoices(); track tag.slug) {
              <div class="form-check ideas-tag-option">
                <input class="form-check-input" type="checkbox" [id]="'ideas-tag-' + tag.slug"
                  [checked]="isTagChosen(tag.slug)" [disabled]="isAnythingLoading()" (change)="toggleTag(tag.slug)">
                <label class="form-check-label" [for]="'ideas-tag-' + tag.slug">
                  <span>{{ tag.name }}</span>
                  <span class="text-muted small flex-shrink-0">{{ tag.count }}</span>
                </label>
              </div>
            }
            @if (state().tags.length) {
              <div class="border-top mt-2 pt-2">
                <button type="button" class="btn btn-link btn-sm p-0" [disabled]="isAnythingLoading()" (click)="clearTags()">{{ t('common.reset') }}</button>
              </div>
            }
          </div>
        </div>
      }
    </div>

    @if (tagsAsPills()) {
      @if (tagChoices().length) {
        <div class="d-flex flex-wrap align-items-center gap-1 mb-3" role="group" [attr.aria-label]="t('ideas.tags')">
          @for (tag of tagChoices(); track tag.slug) {
            <button type="button" class="ideas-tag-pill" role="checkbox" [attr.aria-checked]="isTagChosen(tag.slug)"
              [disabled]="isAnythingLoading()" [title]="tag.name" (click)="toggleTag(tag.slug)">
              <multisite-icon-pill [labelText]="tag.name + ' (' + tag.count + ')'"
                [fallbackIconClasses]="isTagChosen(tag.slug) ? 'fa-solid fa-check' : 'fa-solid fa-tag'"
                [selectable]="true" [active]="isTagChosen(tag.slug)" [disabled]="isAnythingLoading()"
                [shape]="'round'" [compact]="true"></multisite-icon-pill>
            </button>
          }
          @if (state().tags.length) {
            <button type="button" class="btn btn-link btn-sm py-0" [disabled]="isAnythingLoading()" (click)="clearTags()">{{ t('common.reset') }}</button>
          }
        </div>
      } @else {
        <div class="mb-3"></div>
      }
    } @else {
      <div class="d-flex flex-wrap align-items-center gap-1 mb-3">
        @for (tag of chosenTagChips(); track tag.slug) {
          <button type="button" class="ideas-tag-pill" [disabled]="isAnythingLoading()" [title]="tag.name"
            [attr.aria-label]="t('common.remove') + ': ' + tag.name" (click)="toggleTag(tag.slug)">
            <multisite-icon-pill [labelText]="tag.name" [fallbackIconClasses]="'fa-solid fa-tag'"
              [removable]="true" [shape]="'round'" [compact]="true"></multisite-icon-pill>
          </button>
        }
        @if (state().tags.length) {
          <button type="button" class="btn btn-link btn-sm py-0" [disabled]="isAnythingLoading()" (click)="clearTags()">{{ t('common.reset') }}</button>
        }
      </div>
    }
  }

  <div>
      @if (failure(); as failed) {
        @if (failed.notInvolved) {
          <div class="card border-primary">
            <div class="card-body">
              <h2 class="h5"><i class="fa-regular fa-lightbulb me-2"></i>{{ t('ideas.get_involved_title') }}</h2>
              <p class="mb-0">{{ t('ideas.get_involved') }}</p>
            </div>
          </div>
        } @else {
          <div class="alert alert-warning" role="alert">{{ t(failed.translationKey) }}</div>
        }
      } @else if (isAnythingLoading() && !page()) {
        <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
      } @else if (page(); as current) {
        @if (!current.items.length) {
          <div class="alert alert-info" role="status">{{ t('common.no_results') }}</div>
        }
        <div class="list-group mb-3">
          @for (idea of current.items; track idea.id) {
            <a class="list-group-item list-group-item-action d-flex gap-3" [routerLink]="[...baseRoute, idea.slug]">
              <!-- The 16:9 picture the backend chose (IdeaThumbnail); every kind fills the same box. -->
              <div class="idea-thumbnail" aria-hidden="true">
                @switch (idea.thumbnail.kind) {
                  @case ('image') {
                    @if (asImage(idea.thumbnail); as picture) {
                      <img [src]="picture.image | imageUrl: thumbnailTransform" [alt]="picture.image.alt_text ?? ''" loading="lazy" />
                    }
                  }
                  @case ('icon') {
                    @if (asIcon(idea.thumbnail); as icon) {
                      @if (icon.iconType === 'fa' && icon.iconIdentifier) {
                        <i [class]="icon.iconIdentifier + ' idea-thumbnail-icon'"></i>
                      } @else if (icon.imgUrl) {
                        <img class="idea-thumbnail-mark" [src]="icon.imgUrl" alt="" loading="lazy" />
                      }
                    }
                  }
                  @case ('flag') {
                    @if (asFlag(idea.thumbnail); as flag) {
                      <img class="idea-thumbnail-flag" [src]="flagBaseUrl + 'flags/' + flag.hash + '.svg'" [alt]="flag.name ?? ''" loading="lazy" />
                    }
                  }
                  @default {
                    <i class="fa-solid fa-lightbulb-on idea-thumbnail-icon" [class]="stageText(idea.stage)"></i>
                  }
                }
              </div>
              <div class="idea-body">
              <div class="d-flex justify-content-between align-items-start gap-2">
                <span class="fw-bold">{{ idea.name }}</span>
                <span class="d-flex gap-1 flex-shrink-0">
                  @if (idea.pending) {
                    <span class="badge text-bg-warning">{{ t('ideas.relevance.awaiting_approval') }}</span>
                  } @else {
                    <span class="badge" [class]="stageBadge(idea.stage)">{{ t(statusKey(idea.status)) }}</span>
                  }
                  @if (idea.visibility === 'hub') {
                    <span class="badge text-bg-light border"><i class="fa-regular fa-lock me-1"></i>{{ idea.hub?.name }}</span>
                  }
                </span>
              </div>
              @if (idea.summary) {
                <div class="small mt-1">{{ idea.summary }}</div>
              }
              <div class="small text-muted mt-1">
                @for (fact of idea.relevance; track fact.key) {
                  <span class="me-2">{{ t('ideas.relevance.' + fact.key, { count: fact.count ?? 0 }) }}</span>
                }
                @if (idea.commentsCount) {
                  <span class="me-2"><i class="fa-regular fa-comment me-1"></i>{{ idea.commentsCount }}</span>
                }
                @if (idea.createdAt) {
                  <span>{{ idea.createdAt | date: 'mediumDate' }}</span>
                }
              </div>
              </div>
            </a>
          }
        </div>
        @if (current.meta.last_page > 1) {
          <frontend-pagination-type-three [classes]="'justify-content-center'" [loading]="isAnythingLoading()"
            [meta]="current.meta" [lastPage]="current.meta.last_page" (changePage)="goToPage($event)">
          </frontend-pagination-type-three>
        }
      }
  </div>
</div>
